<script setup lang="ts">
import type { BlogPostProps } from '#ui/types';
import type { ISbStoryData } from '@storyblok/js';
import lodashToNumber from 'lodash-es/toNumber';

import type { StoryblokBlogListing, StoryblokMaybeLivePreview } from '#shared/types/storyblok.type';

const ITEMS_PER_PAGE = 10;

// PROPS
export interface StoryblokBlogListingProps {
    blok: StoryblokMaybeLivePreview<StoryblokBlogListing>;
}

const { blok } = defineProps<StoryblokBlogListingProps>();

// COMPOSABLES
const storyblokApi = useStoryblokApi();
const { td, locale } = useTypedI18n();
const { blogApiOptions } = useStoryblokBlogApiParams(() => blok.categories);
const route = useRoute();

// DATA
const page = computed(() => lodashToNumber(route.query.page) || 1);
const { data } = await useAsyncData(
    `blog-listing-${locale.value}-${blok.categories?.join('-') || 'all'}`,
    () =>
        storyblokApi.getStories({
            ...blogApiOptions.value,
            sort_by: 'first_published_at:desc',
            page: page.value,
            per_page: ITEMS_PER_PAGE,
        }),
    {
        watch: [page],
    }
);

const posts = computed<(BlogPostProps & { categories: string[] })[]>(() =>
    (data.value?.data?.stories ?? []).map(story => ({
        title: story.content.title,
        description: story.content.excerpt,
        image: {
            provider: 'storyblok',
            src: story.content.cover_image.filename,
        },
        date: story.first_published_at ? new Date(Date.parse(story.first_published_at)) : undefined,
        to: storyblokGetStoryUrl(story),
        categories: (story.content.categories ?? []).map((category: ISbStoryData<StoryblokBlogPost>) => category.name),
    }))
);

const pagination = computed(() => ({
    page: page.value,
    itemsPerPage: lodashToNumber(data.value?.headers['per-page'] ?? ITEMS_PER_PAGE),
    total: lodashToNumber(data.value?.headers['total'] ?? 0),
}));
const breadcrumbs = await useStoryblokBlogBreadcrumbs(resolveStoryblokSlug(route.params.slug), blok.title);

// SEO
useSchemaOrg([
    defineWebPage({
        '@type': 'CollectionPage',
    }),
    defineBreadcrumb({
        itemListElement: breadcrumbs.value.map((b, i) => ({ name: b.label, item: String(b.to), position: i + 1 })),
    }),
]);
</script>

<template>
    <BaseWidthContainer v-editable="blok">
        <UPageHeader :title="blok.title" :description="blok.excerpt" class="py-[50px]">
            <template #headline>
                <BaseBreadcrumb :items="breadcrumbs" />
            </template>
        </UPageHeader>

        <UPageBody>
            <UBlogPosts>
                <UBlogPost
                    v-for="(post, index) in posts"
                    v-bind="post"
                    :key="index"
                    :orientation="index === 0 ? 'horizontal' : 'vertical'"
                    :class="[index === 0 && 'col-span-full']"
                    variant="naked"
                    :ui="{
                        description: 'line-clamp-2',
                    }"
                >
                    <template v-if="post.categories?.length" #badge>
                        <UBadge v-for="(category, i) in post.categories" :key="i" color="neutral" variant="subtle" :label="category" />
                    </template>
                    <template v-if="post.date" #date>
                        <ClientOnly>
                            {{ td(post.date, 'ddmmyyyy') }}
                        </ClientOnly>
                    </template>
                </UBlogPost>
            </UBlogPosts>
            <BasePagination
                v-if="pagination.total > 0"
                v-bind="pagination"
                size="lg"
                class="w-full justify-center md:border-t md:border-neutral-300 md:pt-8"
            />
        </UPageBody>
    </BaseWidthContainer>
</template>
